import React from 'react';
import { Spinner } from './Spinner';
import { Button } from './Button';
import { Icon } from './Icon';
import './StatusState.css';

export interface LoadingStateProps {
  /** Loading message */
  message?: string;
  /** Spinner size */
  size?: 'small' | 'medium' | 'large';
  /** Spinner variant - 'books' shows shuffling books animation */
  variant?: 'primary' | 'secondary' | 'white' | 'warm' | 'rainbow' | 'books';
  /** Additional CSS class */
  className?: string;
}

/**
 * Loading state component with spinner and message.
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  size = 'medium',
  variant = 'primary',
  className = '',
}) => {
  return (
    <div className={`stc-status stc-status--loading ${className}`}>
      <Spinner size={size} variant={variant} />
      {message && <p className="stc-status__message">{message}</p>}
    </div>
  );
};

export interface EmptyStateProps {
  /** Empty state title */
  title?: string;
  /** Empty state description */
  description?: string;
  /** Icon or emoji to display */
  icon?: React.ReactNode;
  /** Action button */
  action?: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Empty state component for when there's no content to display.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'No results found',
  description,
  icon = <Icon name="inbox" size="xl" />,
  action,
  className = '',
}) => {
  return (
    <div className={`stc-status stc-status--empty ${className}`}>
      {icon && <div className="stc-status__icon">{icon}</div>}
      {title && <h3 className="stc-status__title">{title}</h3>}
      {description && <p className="stc-status__description">{description}</p>}
      {action && <div className="stc-status__action">{action}</div>}
    </div>
  );
};

export interface ErrorStateProps {
  /** Error title */
  title?: string;
  /** Error message */
  message?: string;
  /** Retry callback */
  onRetry?: () => void;
  /** Retry button text */
  retryLabel?: string;
  /** Additional content below error (e.g., alternative actions) */
  children?: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Error state component for displaying error messages.
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  onRetry,
  retryLabel = 'Try again',
  children,
  className = '',
}) => {
  return (
    <div className={`stc-status stc-status--error ${className}`}>
      <div className="stc-status__icon stc-status__icon--error">
        <Icon name="alert-circle" size="lg" />
      </div>
      {title && <h3 className="stc-status__title">{title}</h3>}
      {message && <p className="stc-status__description">{message}</p>}
      {onRetry && (
        <div className="stc-status__action">
          <Button variant="secondary" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      )}
      {children}
    </div>
  );
};

export interface SuccessStateProps {
  /** Success title */
  title?: string;
  /** Success message */
  description?: string;
  /** Action button */
  action?: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Success state component for displaying success messages.
 */
export const SuccessState: React.FC<SuccessStateProps> = ({
  title = 'Success!',
  description,
  action,
  className = '',
}) => {
  return (
    <div className={`stc-status stc-status--success ${className}`}>
      <div className="stc-status__icon stc-status__icon--success">
        <Icon name="check-circle" size="lg" />
      </div>
      {title && <h3 className="stc-status__title">{title}</h3>}
      {description && <p className="stc-status__description">{description}</p>}
      {action && <div className="stc-status__action">{action}</div>}
    </div>
  );
};
